/**
 * FullStory's own element classes. They are its contract, so they are named once, here.
 *
 * - `fs-exclude`: the element and everything in it is left out of the recording.
 * - `fs-mask`: text and input values are replaced with a placeholder of the same shape. Inherited.
 * - `fs-unmask`: exposes a masked element's text again. When one element carries both `fs-mask` and
 *   `fs-unmask`, FullStory applies the stricter, and this module does not add `fs-unmask` to an
 *   element that already says `fs-mask`.
 */
export const SESSION_RECORDING_EXCLUDE_CLASS = "fs-exclude";
export const SESSION_RECORDING_MASK_CLASS = "fs-mask";
export const SESSION_RECORDING_UNMASK_CLASS = "fs-unmask";

/**
 * Everything FullStory cannot mask, only exclude.
 *
 * On the web it cannot mask an image, and masking an iframe does not mask what is in it. Canvas is
 * not documented either way, so it is treated like an image. The images here are not decoration:
 * people's photos, company and team logos, and message attachments arrive from the API as data URLs,
 * so a recorded image carries its bytes into the recording itself. Rich text — comments, messages,
 * knowledge base articles — can render an `<img>` of their own.
 */
const UNMASKABLE_MEDIA = "img, canvas, video, iframe, object, embed";

/**
 * The interface's own words: what a control says, what a field is called, what went wrong with it,
 * what a column holds. Readable in a replay, so a replay shows what someone clicked and why it
 * failed, while everything they were looking at stays masked under the `fs-mask` on `<body>`.
 *
 * A component whose label is data — a button naming the person it acts on — carries `fs-mask`
 * itself, and keeps it. Grow this list only with components that cannot hold data by construction:
 * a menu item, a chip, a dialog title or an alert can each name a client, so none of them is here.
 */
const INTERFACE_TEXT = [
  ".MuiButton-root",
  ".MuiTab-root",
  ".MuiFormLabel-root",
  ".MuiFormHelperText-root",
  ".MuiTableHead-root",
].join(", ");

/**
 * What a person types. Masked on the field itself, so a value stays masked inside interface text
 * that is not — a column filter typed into a table header, say.
 */
const ENTERED_VALUES = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

const RULES: ReadonlyArray<{ selector: string; apply: (element: Element) => void }> = [
  { selector: UNMASKABLE_MEDIA, apply: (element) => addClass(element, SESSION_RECORDING_EXCLUDE_CLASS) },
  { selector: ENTERED_VALUES, apply: (element) => addClass(element, SESSION_RECORDING_MASK_CLASS) },
  { selector: INTERFACE_TEXT, apply: unmask },
];

/**
 * Applies the recording rules to the page, and to whatever is added to it later.
 *
 * One observer over the whole document rather than a class at each component: user images reach the
 * page from a dozen components and from rich text nobody here writes, and a rule each author has to
 * remember is a rule the next author forgets.
 *
 * It must be installed before FullStory loads. Mutation observers are told about a change in the
 * order they were created, so this one tags a new element before FullStory's own observer reads it.
 * It also watches `class`, because React re-rendering a `className` would otherwise remove a tag.
 *
 * Returns a function that stops it.
 */
export function applySessionRecordingPrivacy(root: HTMLElement = document.documentElement): () => void {
  applyToTree(root);

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      // A class change can only have removed a tag from the element itself; its descendants keep
      // theirs. Scanning the subtree here would walk the page on every class MUI toggles.
      if (record.type === "attributes") {
        if (record.target instanceof Element) applyToElement(record.target);
        continue;
      }
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) applyToTree(node);
      });
    }
  });
  observer.observe(root, { attributeFilter: ["class"], attributes: true, childList: true, subtree: true });

  return () => observer.disconnect();
}

function applyToTree(root: Element): void {
  applyToElement(root);
  for (const rule of RULES) root.querySelectorAll(rule.selector).forEach(rule.apply);
}

function applyToElement(element: Element): void {
  for (const rule of RULES) {
    if (element.matches(rule.selector)) rule.apply(element);
  }
}

function unmask(element: Element): void {
  if (element.classList.contains(SESSION_RECORDING_MASK_CLASS)) return;
  addClass(element, SESSION_RECORDING_UNMASK_CLASS);
}

function addClass(element: Element, className: string): void {
  // Checked first: adding a class that is already there still raises a mutation, and this observer
  // would hear its own write forever.
  if (!element.classList.contains(className)) element.classList.add(className);
}
